import { formatINR } from "../AddMoneyToUser/addMoneyUtils";

// Display rules for one entry of the Company Wallet statement (see dao/clientWalletDao.js getClientWalletStatement).
// An entry is one EVENT: a policy creation (one leg per insurer), an edit (refunds + fresh debits), or a manual
// top-up / removal. Everything here is display only; the server already did the arithmetic.

export const KIND_LABELS = {
  TOPUP: "Topup",
  REMOVE: "Remove",
  POLICY_DEBIT: "Policy Debit",
  POLICY_EDIT: "Policy Edited",
  POLICY_NET_ZERO: "Charged & Refunded",
  POLICY_REFUND: "Policy Refund",
  OTHER: "Other",
};

export const kindLabel = (kind) => KIND_LABELS[kind] || "Other";

/** credit (money in), debit (money out) or neutral (nets to zero), from the entry's net amount. */
export const entryTone = (entry) => {
  const net = Number(entry?.net) || 0;
  return net > 0 ? "credit" : net < 0 ? "debit" : "neutral";
};

/**
 * Which part of the policy a leg paid for: OD, TP, PA or Motor. Read from the remark the system wrote for the leg,
 * for display only: nothing is decided from it (the entry kind comes from row types on the server).
 */
export const partLabel = (leg) => {
  const remarks = String(leg?.remarks || "");
  if (leg?.transactionType === "TOPUP") return "Topup";
  if (leg?.transactionType === "REMOVE") return "Remove";
  if (leg?.transactionType === "POLICY_DEBIT_REVERSAL") return "Refund";
  if (/^Re-debit/i.test(remarks)) return "Re-charge";
  if (/\(OD\)/i.test(remarks)) return "OD";
  if (/\(TP\)/i.test(remarks)) return "TP";
  if (/^PA Cover/i.test(remarks)) return "PA";
  if (/^Motor Premium/i.test(remarks)) return "Motor";
  return "";
};

/** The short line under a policy entry: who was charged what, e.g. "Reliance OD ₹ 2,018.31 · National TP ₹ 4,248.69". */
export const breakdownText = (entry) => {
  switch (entry?.kind) {
    case "POLICY_DEBIT":
      return entry.legs
        .map((leg) => [leg.companyName, partLabel(leg), formatINR(leg.amount)].filter(Boolean).join(" "))
        .join(" · ");
    case "POLICY_EDIT":
    case "POLICY_NET_ZERO":
      return `Refunded ${formatINR(entry.refunded)} · Charged ${formatINR(entry.debited)}`;
    case "POLICY_REFUND":
      return `Refunded ${formatINR(entry.refunded)}`;
    default:
      return "";
  }
};

/** A policy that still exists can be opened; `referenceId` is its own _id. */
export const canOpenPolicy = (entry) => Boolean(entry?.policy && !entry.policy.deleted && entry.referenceId);

/** The details cell: a main line, an optional second line (vehicle · company · policy number) and whether to link. */
export const describeEntry = (entry) => {
  const policy = entry?.policy;
  const firstRemark = entry?.legs?.[0]?.remarks || "";
  if (entry?.kind === "TOPUP" || entry?.kind === "REMOVE" || entry?.kind === "OTHER") {
    return { primary: firstRemark, secondary: "", muted: false, openable: false };
  }
  if (policy?.deleted) {
    // A creation that failed and was refunded never saved a policy; say what happened, not "deleted".
    return entry.kind === "POLICY_NET_ZERO"
      ? { primary: "Charged and refunded: nothing was taken", secondary: "", muted: true, openable: false }
      : { primary: "Policy deleted", secondary: "", muted: true, openable: false };
  }
  if (policy) {
    return {
      primary: [policy.policyId, policy.customerName].filter(Boolean).join(" · ") || firstRemark || "Policy",
      secondary: [policy.vehicle, policy.company, policy.policyNumber ? `Policy No ${policy.policyNumber}` : ""]
        .filter(Boolean)
        .join(" · "),
      muted: false,
      openable: canOpenPolicy(entry),
    };
  }
  return { primary: firstRemark || "Policy", secondary: "", muted: false, openable: false };
};

/** Each company has its own balance, so a several-company entry has no single "balance after". */
export const hasSingleBalance = (entry) => entry?.balanceAfter !== null && entry?.balanceAfter !== undefined;

const REAL_DIFFERENCE = 0.005;

/** Plain-language warning for a company card whose balance does not match its transaction history; "" when it adds up. */
export const ledgerWarning = (difference) => {
  const diff = Number(difference) || 0;
  if (Math.abs(diff) < REAL_DIFFERENCE) return "";
  return `This balance does not match the transaction history: it is ${formatINR(Math.abs(diff))} ${
    diff > 0 ? "higher" : "lower"
  } than the history shows.`;
};
